// src/pages/Kitab.jsx
import React, { useEffect, useMemo, useState } from "react";
import Modal from "../components/Modal";
import Select from "react-select";
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";

import {
    collection,
    onSnapshot,
    query,
    orderBy,
    doc,
    getDoc,
    setDoc,
    updateDoc,
    serverTimestamp,
} from "firebase/firestore";

import {
    getStorage,
    ref as storageRef,
    uploadBytes,
    getDownloadURL,
    deleteObject,
} from "firebase/storage";

import { db } from "../utils/FirebaseConfig";

const storage = getStorage();
const MAX_IMAGE_SIZE = 1024 * 1024; // 1MB

const CATEGORY_OPTIONS = [
    { value: "Umum", label: "Umum" },
    { value: "Fekah", label: "Fekah" },
    { value: "Tauhid", label: "Tauhid" },
    { value: "Sirah", label: "Sirah" },
    { value: "Hadis", label: "Hadis" },
    { value: "Tafsir", label: "Tafsir" },
    { value: "Tajwid", label: "Tajwid" },
    { value: "Tasauf", label: "Tasauf" },
];

function slugify(text = "") {
    return text
        .toLowerCase()
        .trim()
        .replace(/[’']/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
}
function normalize(text = "") {
    return text.toLowerCase().replace(/\s+/g, " ").trim();
}

async function generateUniqueKitabId(baseSlug) {
    let uniqueId = baseSlug;
    let count = 2;
    while (true) {
        const snap = await getDoc(doc(db, "kitabs", uniqueId));
        if (!snap.exists()) break;
        uniqueId = `${baseSlug}-${count++}`;
    }
    return uniqueId;
}

async function safeDeleteStorage(path) {
    if (!path) return;
    try {
        await deleteObject(storageRef(storage, path));
    } catch (e) {
        // kalau fail delete, jangan block user (mungkin file dah tiada)
        console.warn("⚠️ Failed to delete old cover:", e?.message || e);
    }
}

export default function Kitab({ isMobile }) {
    const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
    const mosqueId = userProfile?.mosqueId || "";
    const mosqueName = userProfile?.namaMasjid || userProfile?.nama || "Masjid";

    const [kitabs, setKitabs] = useState([]);
    const [search, setSearch] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);

    // add/edit state
    const [editingId, setEditingId] = useState(null);
    const [editingOldCoverPath, setEditingOldCoverPath] = useState("");
    const [title, setTitle] = useState("");
    const [author, setAuthor] = useState("");
    const [category, setCategory] = useState("Umum");
    const [aliasesText, setAliasesText] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    // cover image state
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState(""); // dataURL atau URL sedia ada

    useEffect(() => {
        const q = query(collection(db, "kitabs"), orderBy("normalizedTitle", "asc"));
        const unsub = onSnapshot(
            q,
            (snap) => {
                const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setKitabs(rows);
            },
            (err) => {
                console.error("❌ kitabs snapshot error:", err);
                toast.error("Gagal load senarai kitab.");
            }
        );
        return () => unsub();
    }, []);

    const filtered = useMemo(() => {
        const q = normalize(search);
        if (!q) return kitabs;
        return kitabs.filter((k) => {
            const inTitle = normalize(k.title || "").includes(q);
            const inAuthor = normalize(k.author || "").includes(q);
            const inCategory = normalize(k.category || "").includes(q);
            const inAliases =
                Array.isArray(k.aliases) &&
                k.aliases.some((a) => normalize(a).includes(q));
            return inTitle || inAuthor || inCategory || inAliases;
        });
    }, [kitabs, search]);

    const resetForm = () => {
        setEditingId(null);
        setEditingOldCoverPath("");
        setTitle("");
        setAuthor("");
        setCategory("Umum");
        setAliasesText("");
        setCoverFile(null);
        setCoverPreview("");
    };

    const openAdd = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const openEdit = (k) => {
        setEditingId(k.id);
        setEditingOldCoverPath(k.coverPath || "");
        setTitle(k.title || "");
        setAuthor(k.author || "");
        setCategory(k.category || "Umum");
        setAliasesText((k.aliases || []).join(", "));
        setCoverFile(null); // reset - only upload if user selects new
        setCoverPreview(k.coverImage || ""); // show current cover
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        resetForm();
    };

    const handleCoverChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > MAX_IMAGE_SIZE) {
            toast.error("Gambar terlalu besar. Sila upload bawah 1MB.");
            e.target.value = "";
            return;
        }

        setCoverFile(file);
        const reader = new FileReader();
        reader.onload = () => setCoverPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const uploadCoverIfAny = async (kitabId) => {
        if (!coverFile) return { coverImage: "", coverPath: "" };

        const ext = coverFile.name?.split(".").pop()?.toLowerCase() || "jpg";
        const path = `kitabCovers/${kitabId}/${Date.now()}.${ext}`;
        const sRef = storageRef(storage, path);

        await uploadBytes(sRef, coverFile);
        const url = await getDownloadURL(sRef);
        return { coverImage: url, coverPath: path };
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (isSaving) return;

        if (!mosqueId) {
            toast.error("Mosque ID tak jumpa. Sila login semula.");
            return;
        }

        const cleanTitle = title.trim();
        if (!cleanTitle) return toast.error("Sila isi nama kitab.");

        setIsSaving(true);
        try {
            const baseSlug = slugify(cleanTitle);
            if (!baseSlug) return toast.error("Nama kitab tak valid.");

            const aliases = aliasesText
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);

            // payload common
            const basePayload = {
                title: cleanTitle,
                author: author.trim(),
                category: category || "Umum",
                aliases,
                slug: baseSlug,
                normalizedTitle: normalize(cleanTitle),
                createdByMosqueId: mosqueId,
                createdByMosqueName: mosqueName,
                updatedAt: serverTimestamp(),
            };

            if (editingId) {
                // update
                let coverUpdate = {};
                if (coverFile) {
                    // delete old cover first (if exists)
                    await safeDeleteStorage(editingOldCoverPath);
                    coverUpdate = await uploadCoverIfAny(editingId);
                }

                await updateDoc(doc(db, "kitabs", editingId), {
                    ...basePayload,
                    ...(Object.keys(coverUpdate).length ? coverUpdate : {}),
                });

                toast.success("Kitab berjaya dikemaskini!");
            } else {
                // create
                const uniqueId = await generateUniqueKitabId(baseSlug);
                const cover = await uploadCoverIfAny(uniqueId);

                await setDoc(doc(db, "kitabs", uniqueId), {
                    ...basePayload,
                    ...cover,
                    createdAt: serverTimestamp(),
                    status: "active",
                });

                toast.success("Kitab berjaya ditambah!");
            }

            closeModal();
        } catch (err) {
            console.error("❌ save kitab error:", err);
            toast.error("Gagal simpan kitab.");
        } finally {
            setIsSaving(false);
        }
    };



    return (
        <div style={{ padding: isMobile ? 12 : 18 }}>

            {/* ✅ Penerangan ringkas seksyen Kitab */}
            <div
                style={{
                    background: "#f6f8ff",
                    border: "1px solid #e6eaff",
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 12,
                    color: "#333",
                    lineHeight: 1.4,
                    fontSize: 13,
                }}
            >
                <div style={{ fontWeight: 800, marginBottom: 6 }}>Seks­yen Kitab (Pustaka Rujukan Kuliah)</div>
                <div>
                    Kitab ialah senarai rujukan rasmi untuk kuliah di masjid. Bila masjid pilih kitab semasa tambah kuliah,
                    sistem akan simpan nama kitab + kategori + cover, jadi jadual kuliah lebih kemas dan seragam. Ini juga
                    memudahkan carian, laporan (contoh: kuliah Tafsir sepanjang tahun), dan boleh dikongsi/standardkan jika
                    masjid ingin guna rujukan yang sama setiap bulan. Alias membantu kalau orang tulis ejaan berbeza
                    (contoh: Riyadus Salihin / Riyadhus Shalihin).
                </div>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                    type="text"
                    placeholder="Cari kitab / author / kategori..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ padding: 10, flex: 1 }}
                />
                <button className="button" onClick={openAdd} style={{ margin: 0 }}>
                    Tambah
                </button>
            </div>

            <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                {filtered.map((k) => (
                    <div
                        key={k.id}
                        style={{
                            background: "#fff",
                            borderRadius: 10,
                            padding: 12,
                            border: "1px solid #eee",
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            alignItems: "center",
                        }}
                    >
                        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
                            <div
                                style={{
                                    width: 50,
                                    height: 75,
                                    borderRadius: 4,
                                    overflow: "hidden",
                                    border: "1px solid #eee",
                                    background: "#fafafa",
                                    flex: "0 0 auto",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {k.coverImage ? (
                                    <img
                                        src={k.coverImage}
                                        alt="cover"
                                        style={{ width: "100%", height: "100%", objectFit: "fill" }}
                                    />
                                ) : (
                                    <span style={{ fontSize: 10, color: "#aaa" }}>No Cover</span>
                                )}
                            </div>

                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                                    {k.title}
                                </div>
                                <div style={{ fontSize: 13, color: "#7A1777", marginBottom: 6 }}>
                                    {k.category || "Umum"} {k.author ? `• ${k.author}` : ""}
                                </div>
                                {!!(k.aliases && k.aliases.length) && (
                                    <div style={{ fontSize: 12, color: "#888" }}>
                                        Alias: {k.aliases.join(", ")}
                                    </div>
                                )}
                                <div style={{ fontSize: 11, color: "#bbb", marginTop: 8 }}>
                                    Ditambah oleh: <b>{k.createdByMosqueName || "Masjid"}</b>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <button className="button" onClick={() => openEdit(k)} style={{ padding: "8px 10px" }}>
                                Edit
                            </button>

                        </div>
                    </div>
                ))}

                {filtered.length === 0 && (
                    <div style={{ color: "#888", marginTop: 20 }}>
                        Tiada kitab dijumpai.
                    </div>
                )}
            </div>

            <Modal isOpen={isModalOpen} closeModal={closeModal}>
                <form onSubmit={handleSave} style={{ padding: 10, minWidth: isMobile ? 280 : 440 }}>
                    <h3 style={{ marginTop: 0 }}>
                        {editingId ? "Edit Kitab" : "Tambah Kitab"}
                    </h3>

                    {/* Cover */}
                    <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
                        <div
                            style={{
                                width: 90,
                                height: 90,
                                borderRadius: 12,
                                overflow: "hidden",
                                border: "1px solid #eee",
                                background: "#fafafa",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flex: "0 0 auto",
                            }}
                        >
                            {coverPreview ? (
                                <img
                                    src={coverPreview}
                                    alt="preview"
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                            ) : (
                                <span style={{ fontSize: 11, color: "#aaa" }}>Cover</span>
                            )}
                        </div>

                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 13 }}>
                                Gambar Kitab (optional)
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleCoverChange}
                                    style={{ display: "block", marginTop: 6 }}
                                />
                            </label>
                            <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>
                                * Disaran bawah 1MB (thumbnail cukup).
                            </div>
                        </div>
                    </div>

                    <label style={{ fontSize: 13 }}>
                        Nama Kitab
                        <input
                            className="input-group"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Contoh: Riyadus Salihin"
                            style={{ width: "100%", height: 44, padding: 10, marginTop: 6, marginBottom: 10 }}
                        />
                    </label>

                    <label style={{ fontSize: 13 }}>
                        Kategori (subjek)
                        <div style={{ marginTop: 6, marginBottom: 10 }}>
                            <Select
                                value={CATEGORY_OPTIONS.find((o) => o.value === category)}
                                onChange={(opt) => setCategory(opt?.value || "Umum")}
                                options={CATEGORY_OPTIONS}
                                classNamePrefix="select"
                            />
                        </div>
                    </label>

                    <label style={{ fontSize: 13 }}>
                        Author (optional)
                        <input
                            className="input-group"
                            value={author}
                            onChange={(e) => setAuthor(e.target.value)}
                            placeholder="Contoh: Imam an-Nawawi"
                            style={{ width: "100%", height: 44, padding: 10, marginTop: 6, marginBottom: 10 }}
                        />
                    </label>

                    <label style={{ fontSize: 13 }}>
                        Alias (optional, pisahkan dengan koma)
                        <input
                            className="input-group"
                            value={aliasesText}
                            onChange={(e) => setAliasesText(e.target.value)}
                            placeholder="Contoh: Riyadhus Shalihin, رياض الصالحين"
                            style={{ width: "100%", height: 44, padding: 10, marginTop: 6, marginBottom: 14 }}
                        />
                    </label>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                        <button type="button" className="btn-secondary" onClick={closeModal}>
                            Kembali
                        </button>
                        <button className="btn-primary" type="submit" disabled={isSaving}>
                            {isSaving ? "Menyimpan..." : "Simpan"}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
